import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  TablePagination,
  TextField,
  InputAdornment,
  TableSortLabel,
  Menu,
  MenuItem,
  Avatar,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  DeleteOutline as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  EmojiEvents as RewardIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import CustomerDetailsModal from "./CustomerDetailsModal";

const API_IMAGE_BASE = "https://api.mrbikedoctor.cloud/";

const CustomerTable = ({ datas, loading, onRefresh }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredData = useMemo(() => {
    let result = datas || [];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          item.first_name?.toLowerCase().includes(term) ||
          item.last_name?.toLowerCase().includes(term) ||
          item.email?.toLowerCase().includes(term) ||
          item.phone?.toString().includes(term) ||
          item.customerId?.toLowerCase().includes(term) ||
          item.referralCode?.toLowerCase().includes(term)
      );
    }
    return [...result].sort((a, b) => {
      let valueA = a[orderBy] || "";
      let valueB = b[orderBy] || "";
      if (orderBy === "createdAt") {
        valueA = new Date(valueA).getTime();
        valueB = new Date(valueB).getTime();
      }
      return order === "asc"
        ? valueA < valueB ? -1 : valueA > valueB ? 1 : 0
        : valueB < valueA ? -1 : valueB > valueA ? 1 : 0;
    });
  }, [datas, searchTerm, order, orderBy]);

  const currentData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const getAvatarUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${API_IMAGE_BASE}${imagePath}`;
  };

  const getInitials = (customer) => {
    const f = customer?.first_name?.[0] || "";
    const l = customer?.last_name?.[0] || "";
    return (f + l).toUpperCase() || "C";
  };

  const formatDate = (d) => {
    if (!d) return "N/A";
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleActionClick = (event, customer) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleActionClose = () => setAnchorEl(null);

  const handleOpenProfile = () => {
    setProfileDialogOpen(true);
    handleActionClose();
  };

  const handleCloseDialog = () => {
    setProfileDialogOpen(false);
  };

  const headers = [
    { id: "id", label: "#", sortable: false },
    { id: "first_name", label: "Customer (ID)", sortable: true },
    { id: "email", label: "Email", sortable: true },
    { id: "phone", label: "Phone", sortable: false },
    { id: "address", label: "Location", sortable: false },
    { id: "isProfile", label: "Profile", sortable: false },
    { id: "reward_points", label: "Rewards", sortable: true },
    { id: "referralCode", label: "Referral Code", sortable: true },
    { id: "joinedViaReferral", label: "Joined Via Referral", sortable: true },
    { id: "referralCodeUsed", label: "Referral Code Used", sortable: true },
    { id: "createdAt", label: "Joined", sortable: true },
    { id: "actions", label: "Action", sortable: false },
  ];

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      {/* Search bar */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, flexWrap: "wrap", gap: 1 }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search by name, email, phone, ID or referral code…"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: "100%", sm: 340 } }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          {filteredData.length} customer{filteredData.length !== 1 ? "s" : ""}
        </Typography>
      </Box>

      <TableContainer
        component={Paper}
        elevation={3}
        sx={{
          borderRadius: 2,
          overflowX: "auto",
          "&::-webkit-scrollbar": { height: "8px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#ccc", borderRadius: "10px" },
        }}
      >
        <Table size="small" sx={{ minWidth: 1300 }}>
          <TableHead sx={{ backgroundColor: "#2e83ff" }}>
            <TableRow>
              {headers.map((h) => (
                <TableCell key={h.id} sx={{ fontWeight: "bold", py: 1.5, whiteSpace: "nowrap", color: "white" }}>
                  {h.sortable ? (
                    <TableSortLabel
                      active={orderBy === h.id}
                      direction={orderBy === h.id ? order : "asc"}
                      onClick={() => handleRequestSort(h.id)}
                      sx={{
                        color: "white !important",
                        "& .MuiTableSortLabel-icon": { color: "white !important" },
                      }}
                    >
                      {h.label}
                    </TableSortLabel>
                  ) : h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={headers.length} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={36} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading customers…
                  </Typography>
                </TableCell>
              </TableRow>
            ) : currentData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length} align="center" sx={{ py: 6 }}>
                  <PersonIcon sx={{ fontSize: 40, color: "#ccc", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">No customers found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              currentData.map((customer, index) => (
                <TableRow key={customer._id} hover sx={{ "&:hover": { bgcolor: "#f8faff" } }}>
                  <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                    {page * rowsPerPage + index + 1}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar
                        src={getAvatarUrl(customer.image)}
                        sx={{ width: 34, height: 34, bgcolor: "#2e83ff", fontSize: "0.8rem" }}
                      >
                        {getInitials(customer)}
                      </Avatar>
                      <Box>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setProfileDialogOpen(true);
                          }}
                          sx={{ textTransform: "none", fontWeight: "bold", p: 0, justifyContent: "flex-start", lineHeight: 1.2 }}
                        >
                          {customer.first_name} {customer.last_name}
                        </Button>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {customer.customerId || "ID: N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{customer.email || "N/A"}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption">{customer.phone || "N/A"}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 160, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {[customer.address, customer.city, customer.state].filter(Boolean).join(", ") || "N/A"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={customer.isProfile ? "Complete" : "Incomplete"}
                      color={customer.isProfile ? "success" : "default"}
                      size="small"
                      variant={customer.isProfile ? "filled" : "outlined"}
                      icon={customer.isProfile ? <CheckCircleIcon fontSize="small" /> : undefined}
                      sx={{ fontWeight: "bold", fontSize: "0.65rem" }}
                    />
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <RewardIcon sx={{ fontSize: 14, color: "#f5a623" }} />
                      <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                        {customer.reward_points ?? 0}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                      {customer.referralCode || "-"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={customer.joinedViaReferral ? "Yes" : "No"}
                      color={customer.joinedViaReferral ? "success" : "default"}
                      size="small"
                      variant={customer.joinedViaReferral ? "filled" : "outlined"}
                      sx={{ fontWeight: "bold", fontSize: "0.65rem" }}
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                      {customer.referralCodeUsed || "-"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{formatDate(customer.createdAt)}</Typography>
                  </TableCell>

                  <TableCell>
                    <IconButton size="small" onClick={(e) => handleActionClick(e, customer)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {/* Action dropdown menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleActionClose}>
        <MenuItem onClick={handleOpenProfile}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1, color: "primary.main" }} />
          View Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleActionClose();
            setProfileDialogOpen(true);
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Customer
        </MenuItem>
      </Menu>

      <CustomerDetailsModal
        open={profileDialogOpen}
        customer={selectedCustomer}
        onClose={handleCloseDialog}
      />
    </Box>
  );
};

export default CustomerTable;
